import { useMutation, useQuery } from '@apollo/client';
import { ListAltOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useUuid,
  useScrollIntoView,
  useCustomSnackbar,
  CurrencyProvider,
} from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctDemandRequest } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useHashActionEdit } from '~/hooks/useHashActionEdit/useHashActionEdit';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { useDemandDetail } from '~/modules/Module.hooks';
import { ModuleHash } from '~/modules/Module.types';
import { isDemandEditable } from '~/util/demand/isDemandEditable';

import { DemandRequestDetails } from './components/DemandRequestDetails/DemandRequestDetails';
import { DemandRequestDetailsForm } from './components/DemandRequestDetailsForm/DemandRequestDetailsForm';
import {
  DemandRequestFormData,
  demandRequestFormDataDefault,
  demandRequestFormDataSchema,
  TsrctDemandRequestMapper,
} from './components/DemandRequestDetailsForm/DemandRequestDetailsForm.schema';
import { DEMAND_REQUEST_MUTATION } from './DemandRequestWidget.mutation.gql';
import { DEMAND_REQUEST_QUERY } from './DemandRequestWidget.query.gql';
import { formToDemandRequestInput } from './DemandRequestWidget.util';

const EMPTY_CONTENTDATA = demandRequestFormDataDefault as { demandRequest: TsrctDemandRequest };

export interface DemandRequestWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const DemandRequestWidget = ({ hasParentForm }: DemandRequestWidgetProps) => {
  const { demandId: entityId } = useDemandDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRequestWidget' });
  const [widgetRef, active, hashAction] = useScrollIntoView({ hash: ModuleHash.DEMAND_REQUEST });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const track = useTrackInteraction();
  const formId = useUuid();

  const variables = {
    id: entityId,
  };

  const { data, loading, error } = useQuery(DEMAND_REQUEST_QUERY, {
    variables,
    skip: !entityId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [update, { loading: saving }] = useMutation(DEMAND_REQUEST_MUTATION, {
    refetchQueries: [{ query: DEMAND_REQUEST_QUERY, variables }],
    onError,
  });

  const [editOpen, setEditOpen] = useState(hasParentForm);

  const demand = error ? null : (data?.tesseract?.demand ?? null);
  const contentData = hasParentForm ? EMPTY_CONTENTDATA : demand;
  const request = contentData?.demandRequest;

  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
    track(Target.WIDGET_EDIT, Action.CLICKED, { widget: 'demand_request', entity: 'demand', id: entityId });
  }, [track, entityId]);

  const handleEditApply: FormSchemaProviderProps<DemandRequestFormData>['onSubmit'] = (submitData) => {
    if (submitData) {
      (async () => {
        const { errors } = await update({
          variables: {
            demandId: entityId,
            demandRequest: await formToDemandRequestInput(
              submitData.demandRequest,
              demand?.demandRequest?.sourceReferences,
            ),
          },
        });
        track(Target.WIDGET_SAVE, Action.CLICKED, { widget: 'demand_request', entity: 'demand', id: entityId });
        if (!errors) {
          setEditOpen(false);
          enqueueSuccessSnackbar(t('saveSuccess'));
        }
      })();
    }
  };

  const editDisabled = !demand?.permissions?.write || !isDemandEditable(data);

  useHashActionEdit({
    id: demand?.id,
    active,
    editDisabled,
    editOpen,
    setEditOpen,
    hashAction,
  });

  const sourceReferences = demand?.demandRequest?.sourceReferences;
  const referenceDisabled = !isNil(sourceReferences) && !isEmpty(sourceReferences);

  let content = null;

  const demandRequestFormData = useMemo(() => {
    return contentData ? TsrctDemandRequestMapper.parse(contentData) : demandRequestFormDataDefault;
  }, [contentData]);

  if (editOpen && contentData && !saving) {
    content = <DemandRequestDetailsForm referenceDisabled={referenceDisabled} />;
    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<DemandRequestFormData>
          id={formId}
          data={demandRequestFormData}
          schema={demandRequestFormDataSchema}
          defaultValues={demandRequestFormDataDefault}
          onSubmit={handleEditApply}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else if (loading || (demand && request)) {
    content = (
      <CurrencyProvider value={request?.currency ?? DEFAULT_CURRENCY}>
        <DemandRequestDetails
          data={{
            id: demand?.id,
            requestedBy: demand?.requestedBy,
            request,
          }}
          loading={loading || saving}
        />
      </CurrencyProvider>
    );
  }

  const purchaseRequisitionId = demand?.demandRequest?.externalReference;
  const title = useMemo(() => {
    return (
      <>
        {t('title')}
        {purchaseRequisitionId && (
          <Typography component="span" variant="body2">
            {` ${t('purchaseRequisition', { purchaseRequisitionId })}`}
          </Typography>
        )}
      </>
    );
  }, [purchaseRequisitionId, t]);

  return (
    <Widget ref={widgetRef} active={active} hover={false} data-intercom-target="DemandRequestWidget">
      <WidgetHeader
        title={title}
        icon={<WidgetIcon icon={<ListAltOutlined fontSize="small" />} />}
        actionsRight={
          hasParentForm ? null : (
            <WidgetEditActions
              formId={formId}
              open={editOpen}
              saving={saving}
              disabled={editDisabled}
              onToggle={handleEditToggle}
            />
          )
        }
      />
      <WidgetContent>
        <div>{content}</div>
      </WidgetContent>
    </Widget>
  );
};
